import { supabase } from '../supabase';
import { Game, Recommendation, RecommendationFilters, RecommendationAlgorithm } from '../../types/index';
import { v4 as uuidv4 } from 'uuid';
import { BaseRecommender } from './baseRecommender';
import { DatabaseUtils } from './utils/databaseUtils';
import { GameProcessingUtils } from './utils/gameProcessingUtils';
import { RecommendationDataService } from './services/recommendationDataService';
import { PopularRecommendationService } from '../services/popularRecommendationService';

/**
 * Recomendador baseado em jogos populares
 * com filtragem por categorias favoritas do usuário
 */
export class PopularRecommender extends BaseRecommender {
  private popularService: PopularRecommendationService;

  constructor() {
    super();
    this.popularService = new PopularRecommendationService();
  }

  /**
   * Retorna jogos populares com base nas categorias favoritas do usuário
   */
  public async getRecommendations(
    userId: string,
    count: number,
    excludeGameIds: string[],
    filters?: RecommendationFilters
  ): Promise<Game[]> {
    try {
      // Validar IDs de exclusão usando serviço centralizado
      const validExcludeIds = await this.getCompleteExcludeIds(userId, excludeGameIds);

      // Aplicar filtros inteligentes baseados nas preferências do usuário
      const enhancedFilters = await this.applyDefaultFilters(userId, filters);
      
      console.log(`Popular: buscando ${count} recomendações populares`);
      
      // Buscar jogos populares diretamente do serviço centralizado
      const recommendations: Recommendation[] = await this.popularService.getPopularRecommendations(
        userId,
        count,
        validExcludeIds
      );
      
      const games: Game[] = recommendations.map(rec => rec.game).filter(game => game !== undefined) as Game[];
      
      if (games.length === 0) {
        console.warn('[PopularRecommender] Nenhum jogo popular retornado pelo serviço. Usando fallback da base.');
        return this.getFallbackRecommendations(count, validExcludeIds);
      }
      
      return games.slice(0, count);
    } catch (error) {
      console.error('Erro ao gerar recomendações populares via serviço:', error);
      return this.getFallbackRecommendations(count, excludeGameIds);
    }
  }
  
  /**
   * Busca jogos recentes como complemento
   */
  private async getRecentGamesRecommendations(
    count: number,
    excludeGameIds: string[],
    filters?: RecommendationFilters
  ): Promise<Game[]> {
    try {      
      // Buscar jogos recentes
      const recentGames = await DatabaseUtils.executeSafeQuery(
        { ...filters },
        count * 2,
        'created_at',
        false // descendente
      );
      
      // Filtrar exclusões manualmente
      const filteredGames = GameProcessingUtils.filterExcludedGames(recentGames, excludeGameIds);
      
      if (filteredGames.length < count) {
        return this.getFallbackRecommendations(count, excludeGameIds);
      }
      
      return GameProcessingUtils.shuffleArray(filteredGames).slice(0, count);
    } catch (error) {
      console.error('Erro ao buscar jogos recentes:', error);
      return this.getFallbackRecommendations(count, excludeGameIds);
    }
  }

  /**
   * Obtém apenas os IDs essenciais para excluir (jogos na coleção e disliked)
   * Útil quando precisamos ser menos restritivos para garantir resultados
   */
  private async getEssentialExcludeIds(userId: string): Promise<string[]> {
    try {
      // Usar RecommendationDataService para buscar apenas os não gostados
      const { dislikes } = await RecommendationDataService.getUserFeedback(userId);
      const dislikedIds = Array.from(dislikes);

      // Buscar possuídos diretamente
      const { data: ownedGames, error: ownedError } = await supabase
        .from('user_games')
        .select('game_id')
        .eq('user_id', userId)
        .eq('owned', true);
      
      const ownedIds = (!ownedError && ownedGames) ? ownedGames.map(g => g.game_id) : [];

      return Array.from(new Set([...dislikedIds, ...ownedIds]));
    } catch (error) {
      console.error('Erro ao buscar IDs essenciais de exclusão:', error);
      return [];
    }
  }
} 